/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useRef, CSSProperties } from 'react';
import { useField, FieldValidator } from 'formik';
import { Form, Input } from 'antd';
import classNames from 'classnames';
import { ReactComponent as EditPencil } from '../../../assets/icons/editPencil.svg';
import './form-control.less';
import capitalize from '../../../utils/capitalize';
import { ReactComponent as ThrashCan } from '../../../assets/icons/thrashCan.svg';

interface IFormControlProps {
  label: string;
  wrapperClassName?: string;
  inputClassName?: string;
  name: string;
  type: string;
  disabled?: boolean;
  style?:CSSProperties | undefined;
  editable?: boolean;
  isCurrent?: boolean;
  inputMode?: string;
  onEdit?: () => void;
  onChange?: (e:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onLabelChange?: (e:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  labelFocused?:boolean;
  value?:string;
  placeholder?: string;
  labelName?: string,
  onDelete?: () => void;
  validate?: FieldValidator
}
const FormControl: React.FC<IFormControlProps> = ({
  type,
  label = '',
  wrapperClassName,
  inputClassName,
  disabled,
  placeholder,
  editable = false,
  labelFocused = false,
  inputMode,
  onChange = () => null,
  onDelete,
  onLabelChange = () => null,
  onBlur = () => null,
  validate,
  value,
  labelName,
  style,
  ...props
}: IFormControlProps): JSX.Element => {
  const [field, meta, helpers] = useField({ ...props, validate: validate || undefined });
  const [labelField] = useField(labelName || '');
  const labelRef = useRef();
  const [labelInputActive, setLabelInputActive] = useState(labelFocused);
  let input;
  switch (type) {
    case 'password':
      input = (
        <Input.Password
          type={type}
          name={field.name}
          onChange={(e) => { field.onChange(e); onChange(e); }}
          onBlur={field.onBlur}
          value={field.value || value}
          checked={field.checked}
          className={classNames('form-control__input', inputClassName)}
          style={meta.error ? { border: '1px solid #E13333' } : undefined}
          disabled={disabled}
        />
      );
      break;
    case 'textarea':
      input = (
        <Input.TextArea
          name={field.name}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={(e) => { field.onChange(e); onChange(e); }}
          onBlur={field.onBlur}
          value={value || field.value}
          className={classNames('form-control__input', inputClassName, 'textarea')}
          style={meta.error ? { border: '1px solid #E13333' } : style}
          disabled={disabled}
          autoSize
        />
      );
      break;
    default:
      input = (
        <>
          {editable && (
            <div className="ant-col ant-form-item-label form-control__label">
              {!labelInputActive ? (
                <>
                  <label htmlFor={field.name}>
                    {labelName ? capitalize(labelField.value)
                      : label}

                  </label>
                  <button
                    className="form-control__edit-btn"
                    type="button"
                    onClick={() => {
                      setLabelInputActive(true);
                    }}
                  >
                    <EditPencil />
                  </button>
                </>
              ) : (
                <Input
                  type="text"
                  className="form-control__label-input"
                  name={labelField.name}
                  value={labelName ? `${capitalize(labelField.value)}` : undefined}
                  defaultValue={label}
                  ref={labelRef.current}
                  autoFocus
                  checked={field.checked}
                  placeholder="Label"
                  onChange={(e) => {
                    labelField.onChange(e);

                    helpers.setTouched(true);
                  }}
                  onBlur={(e) => { setLabelInputActive(false); onLabelChange(e); onBlur(e); }}
                />
              )}
            </div>
          )}
          <Input
            type={type}
            name={field.name}
            inputMode={inputMode}
            // eslint-disable-next-line react/jsx-no-bind
            onChange={(e) => {
              field.onChange(e); onChange(e); helpers.setTouched(true); if (validate) {
                helpers.setValue(e.target.value, true);
              }
            }}
            onBlur={field.onBlur}
            value={field.value}
            autoComplete="off"
            placeholder={placeholder}
            checked={field.checked}
            className={classNames('form-control__input', inputClassName)}
            style={meta.error ? { border: '1px solid #E13333' } : undefined}
            disabled={disabled}
          />
        </>
      );
      break;
  }
  return (
    <>
      <Form.Item label={editable ? undefined : label} className={classNames('form-control', wrapperClassName, editable ? 'form-control--editable' : '')}>
        {input}
        {meta.error && (
          <p className="form-control__error">
            {capitalize(meta.error)}
          </p>
        )}
      </Form.Item>
      {onDelete && (
        <button
          className="form__plain-input__delete"
          type="button"
          onClick={() => {
            helpers.setTouched(true);
            onDelete();
          }}
        >
          <ThrashCan />
        </button>
      )}
    </>
  );
};

FormControl.displayName = 'FormControl';

export default FormControl;
